import { createElement, useEffect, useRef } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import { HugeiconsIcon } from '@hugeicons/react'
import { Bookmark02Icon } from '@hugeicons/core-free-icons'
import mapboxgl from 'mapbox-gl'
import { useMapboxMapContext } from '../contexts/MapboxMapContext'
import { WaypointResponse } from '@/shared/api/routes'

export interface UseGpxImportedWaypointMarkersProps {
  waypoints?: WaypointResponse[]
  selectedWaypoint?: WaypointResponse | null
  onWaypointClick?: (waypoint: WaypointResponse) => void
  showLibraryWaypoints?: boolean // Toggle to show/hide library waypoints
  selectedTripId?: string | null // Filter out waypoints already in this trip
}

/**
 * Hook for displaying custom icon markers for library waypoints
 * Uses Hugeicons Bookmark02 for saved (library) waypoints
 * These waypoints have folder_id !== null and trip_id === null
 */
export function useGpxImportedWaypointMarkers({
  waypoints = [],
  selectedWaypoint,
  onWaypointClick,
  showLibraryWaypoints = false,
  selectedTripId = null
}: UseGpxImportedWaypointMarkersProps) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()
  const markersRef = useRef<mapboxgl.Marker[]>([])
  const onWaypointClickRef = useRef(onWaypointClick)
  
  // Store callback in ref to avoid recreating markers when callback changes
  useEffect(() => {
    onWaypointClickRef.current = onWaypointClick
  }, [onWaypointClick])

  // Filter to only library waypoints (folder_id !== null, trip_id === null)
  // Only show if toggle is enabled
  // If selectedTripId is provided, exclude waypoints already in that trip
  const libraryWaypoints = waypoints.filter(wp => {
    // Must be a library waypoint (has folder_id, no trip_id) with valid coordinates
    if (!(wp.folder_id != null && !wp.trip_id && wp.latitude != null && wp.longitude != null)) {
      return false
    }
    
    // Only show if toggle is enabled
    if (!showLibraryWaypoints) {
      return false
    }
    
    // If viewing a specific trip, exclude waypoints already in that trip
    if (selectedTripId && wp.trip_id === selectedTripId) {
      return false
    }
    
    return true
  })

  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded) {
      // Clear markers if map not ready
      markersRef.current.forEach(marker => {
        try {
          marker.remove()
        } catch (e) {
          // Ignore errors if marker was already removed
        }
      })
      markersRef.current = []
      return
    }

    // Clear existing markers
    markersRef.current.forEach(marker => {
      try {
        marker.remove()
      } catch (e) {
        // Ignore errors if marker was already removed
      }
    })
    markersRef.current = []

    // Create custom icon markers for each library waypoint
    libraryWaypoints.forEach((waypoint) => {
      const lat = waypoint.latitude
      const lng = waypoint.longitude

      if (lat == null || lng == null || isNaN(Number(lat)) || isNaN(Number(lng))) {
        return
      }

      const isSelected = selectedWaypoint?.id === waypoint.id

      // Create marker element with icon
      const markerEl = document.createElement('div')
      markerEl.className = 'library-waypoint-marker'
      
      // Different icons for GPX imported vs manually added waypoints
      const iconSize = isSelected ? 28 : 24
      const borderWidth = isSelected ? 3 : 2
      const shadowSize = isSelected ? '0 4px 8px rgba(0,0,0,0.4)' : '0 2px 4px rgba(0,0,0,0.3)'

      const innerPx = iconSize - 8
      const bookmarkSvg = renderToStaticMarkup(
        createElement(HugeiconsIcon, {
          icon: Bookmark02Icon,
          size: innerPx,
          color: 'white'
        })
      )

      markerEl.innerHTML = `
        <div style="
          width: ${iconSize}px;
          height: ${iconSize}px;
          background-color: #5C8DC5;
          border-radius: 50%;
          border: ${borderWidth}px solid white;
          box-shadow: ${shadowSize};
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease-in-out;
        ">
          ${bookmarkSvg}
        </div>
      `
      
      // Add click handler
      markerEl.addEventListener('click', (e) => {
        e.stopPropagation()
        e.preventDefault()
        if (onWaypointClickRef.current) {
          onWaypointClickRef.current(waypoint)
        }
      })

      const marker = new mapboxgl.Marker({
        element: markerEl,
        anchor: 'center'
      })
        .setLngLat([lng, lat])
        .addTo(map.current!)

      markersRef.current.push(marker)
    })

    return () => {
      markersRef.current.forEach(marker => {
        try {
          marker.remove()
        } catch (e) {
          // Ignore errors if marker was already removed
        }
      })
      markersRef.current = []
    }
  }, [map, isLoaded, styleLoaded, libraryWaypoints, selectedWaypoint])

  return { gpxImportedWaypoints: libraryWaypoints }
}

